if (!customElements.get('soso-gifting')) customElements.define('soso-gifting', class extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.message = this.querySelector('[data-gift-message]');
    this.error = this.querySelector('[data-gift-error]');
    this.status = this.querySelector('[data-gift-status]');
    this.form = this.closest('form');
    this.savedMessage = this.message.value;
    this.queue = Promise.resolve();
    this.message.addEventListener('input', () => {
      this.status.textContent = '';
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => this.saveNote().catch(() => {}), 450);
    });
    this.message.addEventListener('change', () => this.saveNote().catch(() => {}));
    this.querySelector('[data-add-gift]')?.addEventListener('click', () => this.addGift());
    this.onSubmit = event => {
      if (this.submitting) return;
      event.preventDefault();
      const submitter = event.submitter;
      this.saveNote()
        .then(() => new Promise(resolve => setTimeout(resolve, 0)))
        .then(() => {
          this.submitting = true;
          try { this.form.requestSubmit(submitter); }
          finally { this.submitting = false; }
        })
        .catch(() => {});
    };
    this.form?.addEventListener('submit', this.onSubmit);
    this.onNavigate = event => {
      const link = event.target.closest('a[href]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || this.savedMessage === this.message.value.trim()) return;
      event.preventDefault();
      this.saveNote().then(() => { location.href = link.href; }).catch(() => {});
    };
    this.form?.addEventListener('click', this.onNavigate);
  }
  disconnectedCallback() { clearTimeout(this.noteTimer); this.form?.removeEventListener('submit', this.onSubmit); this.form?.removeEventListener('click', this.onNavigate); this.ready = false; }
  async request(endpoint, data) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(root + endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
    const result = await response.json();
    if (!response.ok) throw new Error(this.dataset.error);
    return result;
  }
  showError(error) { this.error.hidden = false; this.error.textContent = error.message || this.dataset.error; }
  saveNote() {
    const value = this.message.value.trim();
    this.queue = this.queue.catch(() => {}).then(async () => {
      if (value === this.savedMessage) return;
      this.error.hidden = true;
      try {
        await this.request('cart/update.js', { attributes: { 'Gift message': value } });
        this.savedMessage = value;
        this.status.textContent = this.dataset.saved;
      } catch (error) { this.showError(error); throw error; }
    });
    return this.queue;
  }
  async addGift() {
    const button = this.querySelector('[data-add-gift]');
    if (this.busy || button.disabled || !this.dataset.variant) return;
    this.busy = true; button.disabled = true; this.error.hidden = true;
    try {
      await this.saveNote();
      await this.request('cart/add.js', { items: [{ id: Number(this.dataset.variant), quantity: 1, properties: { '_soso_gift_box': 'true' } }] });
      location.reload();
    } catch (error) { this.showError(error); button.disabled = false; }
    finally { this.busy = false; }
  }
});

// Keep native inline purchase controls and explicit-size validation.
document.querySelectorAll('.soso-product-page').forEach(section => {
  if (section.dataset.purchaseReady) return;
  const primary = section.querySelector('[data-primary-purchase]');
  const button = primary;
  const form = primary?.form;
  const controls = primary?.closest('.product-form-row');
  if (!primary || !form || !controls) return;
  section.dataset.purchaseReady = 'true';
  controls.classList.add('soso-purchase-controls');
  const initiallyDisabled = primary.disabled;
  const picker = section.querySelector('[data-size-picker]');
  const radios = [...(picker?.querySelectorAll('input[type="radio"]') || [])];
  const bundle = section.querySelector('soso-fixed-bundle-picker');
  const shared = bundle?.querySelector('[data-bundle-size-all]');
  const mixed = bundle?.querySelector('[data-bundle-mixed]');
  const individual = [...(bundle?.querySelectorAll('[data-bundle-option]') || [])];
  const variant = section.querySelector('[data-variant-select]');
  const arabic = document.documentElement.lang.startsWith('ar');
  const chooseText = arabic ? 'اختاري المقاس' : 'Choose a size';
  const addingText = arabic ? 'جارٍ الإضافة…' : 'Adding…';
  const confirmed = new Set();
  let chosen = false;
  let busy = false;
  let prompted = false;
  let attention;
  const choiceKey = 'soso_explicit_pattern_size';
  // Only carry a choice through a shopper's actual pattern click, never infer
  // intent from the first variant or from an automatically generated variant URL.
  try {
    const carried = JSON.parse(sessionStorage.getItem(choiceKey));
    sessionStorage.removeItem(choiceKey);
    chosen = !!(carried && carried.path === location.pathname && carried.variant === variant?.value && Date.now() - carried.at < 60000);
  } catch { /* Reconfirm the size when session storage is unavailable. */ }
  const missing = () => {
    if (bundle) {
      if (mixed?.checked) return individual.find(control => !control.value || !confirmed.has(control));
      return !shared?.value || !confirmed.has(shared) ? shared : null;
    }
    return radios.length && !chosen ? radios.find(control => control.checked && !control.disabled) || radios.find(control => !control.disabled) : null;
  };
  const sync = () => {
    const needsSize = !!missing();
    const canChoose = !variant || [...variant.options].some(option => option.dataset.available === 'true');
    const unavailable = variant ? variant.selectedOptions[0]?.dataset.available !== 'true' : initiallyDisabled;
    const label = busy ? addingText : needsSize ? (prompted ? chooseText : primary.dataset.add) : unavailable ? primary.dataset.sold : primary.dataset.add;
    const disabled = busy || (needsSize ? !canChoose : unavailable);
    if (label && button.textContent !== label) button.textContent = label;
    if (button.disabled !== disabled) button.disabled = disabled;
    button.setAttribute('aria-busy', String(busy));
  };
  const clearAttention = () => {
    attention?.classList.remove('soso-size-attention');
    attention = null;
    prompted = false;
  };
  const showSize = control => {
    clearAttention();
    prompted = true;
    attention = control.closest('.size-options,.soso-bundle-field,.soso-bundle-shared') || control;
    attention.classList.add('soso-size-attention');
    control.focus({ preventScroll: true });
    attention.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    sync();
  };
  const recordChoice = event => {
    if (!event.isTrusted) return;
    if (radios.includes(event.target)) chosen = true;
    else if (event.target === shared && event.type === 'change') {
      if (shared.value) { confirmed.add(shared); individual.forEach(control => confirmed.add(control)); }
      else confirmed.clear();
    } else if (individual.includes(event.target) && event.type === 'change') {
      confirmed.add(event.target);
      if (individual.every(control => control.value && confirmed.has(control)) && individual.every(control => control.value === individual[0].value)) confirmed.add(shared);
    } else if (event.target !== mixed) return;
    clearAttention();
    // Existing selector handlers update the native variant/availability first.
    queueMicrotask(sync);
  };
  section.addEventListener('click', recordChoice);
  section.addEventListener('change', recordChoice);
  section.addEventListener('click', event => {
    const link = event.target.closest('[data-pattern-link]');
    if (!link) return;
    try {
      sessionStorage.removeItem(choiceKey);
      if (chosen) {
        const url = new URL(link.href);
        sessionStorage.setItem(choiceKey, JSON.stringify({ path: url.pathname, variant: url.searchParams.get('variant'), at: Date.now() }));
      }
    } catch { /* The destination safely asks for a size again. */ }
  });
  button.addEventListener('click', event => {
    const control = missing();
    if (control || busy) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (control) showSize(control);
    }
  }, true);
  // Guard both entry points against accidental default-size and repeated submits.
  form.addEventListener('submit', event => {
    const control = missing();
    if (control || busy || primary.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (control) showSize(control);
      return;
    }
    busy = true;
    primary.setAttribute('aria-busy', 'true');
    sync();
    queueMicrotask(() => {
      if (event.defaultPrevented) { busy = false; primary.removeAttribute('aria-busy'); sync(); }
    });
  }, true);
  new MutationObserver(sync).observe(primary, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  section.addEventListener('change', () => queueMicrotask(sync));
  window.addEventListener('pageshow', () => { busy = false; primary.removeAttribute('aria-busy'); sync(); });
  sync();
});